import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Spinner } from 'react-bootstrap';
import { FaCar, FaCog } from 'react-icons/fa';
import { API_URL } from './config';

/**
 * Real, live-wired control for two things that only existed as hardcoded backend values before
 * 2026-09-15 (see xgo_rideshare_backend's KNOWN_ISSUES_AND_FIX_BACKLOG.md #68 and
 * migrations/add_vehicle_ownership_tiers.sql / add_configurable_earnings_allocation_rates.sql):
 *
 * 1. Per-driver vehicle ownership/management status — gates which earnings-allocation deductions
 *    (fuel, vehicle rental, vehicle maintenance) apply on top of platform commission. Calls the
 *    real GET/PUT /vehicle-ownership/:driverId endpoints.
 * 2. The allocation rates themselves (fuel $/liter, km/liter, vehicle rental %, vehicle
 *    maintenance %) — calls the real GET/PUT /commission/settings endpoints (extended, not
 *    duplicated — platform commission % already lived here).
 *
 * Deliberately a separate, small, real component rather than added to VehicleRentalManagement.js
 * on this page — that existing screen is currently 100% mock data (its own fetchManagementData
 * comment says so) for a larger, different, not-yet-real "vehicle financing" feature; bolting a
 * real feature onto a mock one risked either rewriting that whole screen (out of scope) or
 * silently mixing real and fake data. See this session's report for the recommended follow-up:
 * consolidate all financial dashboard screens (this one, VehicleRentalManagement, Billing,
 * DriverEarningsPanel, TellerControlPanel, Promotions) into one real "Financial Settings" area.
 */
const TIERS = [
  {
    value: 'independent',
    label: 'Independent (driver owns & manages)',
    description: 'Commission only — the driver keeps everything else. This is the default for every driver.'
  },
  {
    value: 'owner_financed_xgo_managed',
    label: 'Owner-financed, XGO-managed',
    description: 'Commission + maintenance (to XGO) + a rental-sized amount collected on behalf of the vehicle’s real owner, into their own separate withdrawable balance.'
  },
  {
    value: 'xgo_owned_managed',
    label: 'XGO-owned & managed',
    description: 'Full split: commission + fuel program + vehicle rental + vehicle maintenance (all to XGO).'
  },
];

const VehicleOwnershipSettings = () => {
  const [driverId, setDriverId] = useState('');
  const [status, setStatus] = useState(null); // {tier, owner_id}
  const [tierChoice, setTierChoice] = useState('independent');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(true);
  const [savingRates, setSavingRates] = useState(false);
  const [ratesMessage, setRatesMessage] = useState(null);

  const fetchRates = useCallback(async () => {
    setLoadingRates(true);
    try {
      const res = await fetch(`${API_URL}/commission/settings`);
      const data = await res.json();
      setRates(data.settings);
    } catch (e) {
      setRatesMessage({ type: 'danger', text: 'Failed to load current rates.' });
    } finally {
      setLoadingRates(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const lookupDriver = async () => {
    if (!driverId.trim()) return;
    setLoadingStatus(true);
    setStatusMessage(null);
    try {
      const res = await fetch(`${API_URL}/vehicle-ownership/${encodeURIComponent(driverId.trim())}`);
      const data = await res.json();
      setStatus(data);
      setTierChoice(data.tier || 'independent');
    } catch (e) {
      setStatusMessage({ type: 'danger', text: 'Failed to look up this driver.' });
    } finally {
      setLoadingStatus(false);
    }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    setStatusMessage(null);
    try {
      const body = { tier: tierChoice, admin_id: 'dashboard-admin' };
      if (tierChoice === 'owner_financed_xgo_managed') {
        if (status?.owner_id) {
          body.owner_id = status.owner_id;
        } else if (!ownerName.trim()) {
          setStatusMessage({ type: 'warning', text: 'Owner name is required for this tier.' });
          setSavingStatus(false);
          return;
        } else {
          body.owner_name = ownerName.trim();
          body.owner_phone = ownerPhone.trim();
        }
      }
      const res = await fetch(`${API_URL}/vehicle-ownership/${encodeURIComponent(driverId.trim())}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      setStatus(data);
      setStatusMessage({ type: 'success', text: `Updated — this driver is now "${TIERS.find(t => t.value === data.tier)?.label}". Applies to trips completed from now on.` });
    } catch (e) {
      setStatusMessage({ type: 'danger', text: e.message });
    } finally {
      setSavingStatus(false);
    }
  };

  const saveRates = async () => {
    setSavingRates(true);
    setRatesMessage(null);
    try {
      const res = await fetch(`${API_URL}/commission/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          percentage_commission_rate: rates.percentage_commission_rate,
          daily_flat_figure_commission: rates.daily_flat_figure_commission,
          daily_promotion_active: rates.daily_promotion_active,
          promotion_start_date: rates.promotion_start_date,
          promotion_end_date: rates.promotion_end_date,
          lock_to_percentage: rates.lock_to_percentage,
          auto_switch_percentage: rates.auto_switch_percentage,
          fuel_price_per_liter: parseFloat(rates.fuel_price_per_liter),
          km_per_liter: parseFloat(rates.km_per_liter),
          vehicle_rental_percentage: parseFloat(rates.vehicle_rental_percentage),
          vehicle_maintenance_percentage: parseFloat(rates.vehicle_maintenance_percentage),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update rates');
      setRatesMessage({ type: 'success', text: 'Rates updated — takes effect on the next trip completion, no restart needed.' });
      fetchRates();
    } catch (e) {
      setRatesMessage({ type: 'danger', text: e.message });
    } finally {
      setSavingRates(false);
    }
  };

  const effectiveFuelRate = rates && rates.km_per_liter > 0
    ? (parseFloat(rates.fuel_price_per_liter) / parseFloat(rates.km_per_liter)).toFixed(4)
    : null;

  return (
    <div style={{ padding: '24px' }}>
      <h3><FaCar style={{ marginRight: 8 }} />Vehicle Ownership & Earnings Allocation Settings</h3>
      <p className="text-muted">
        Controls what a driver's fare split looks like beyond platform commission, and the real
        rates that split uses. See <code>KNOWN_ISSUES_AND_FIX_BACKLOG.md</code> #68 for why these
        used to be hardcoded and unsafe to change without a code deploy.
      </p>

      <Card className="mb-4">
        <Card.Header><FaCog style={{ marginRight: 8 }} />Allocation Rates (applies to every driver)</Card.Header>
        <Card.Body>
          {loadingRates && <Spinner animation="border" size="sm" />}
          {!loadingRates && rates && (
            <>
              {ratesMessage && <Alert variant={ratesMessage.type}>{ratesMessage.text}</Alert>}
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fuel price per liter ($)</Form.Label>
                    <Form.Control
                      type="number" step="0.01" value={rates.fuel_price_per_liter}
                      onChange={(e) => setRates({ ...rates, fuel_price_per_liter: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Average km per liter</Form.Label>
                    <Form.Control
                      type="number" step="0.1" value={rates.km_per_liter}
                      onChange={(e) => setRates({ ...rates, km_per_liter: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vehicle rental % (of available)</Form.Label>
                    <Form.Control
                      type="number" step="0.01" min="0" max="1" value={rates.vehicle_rental_percentage}
                      onChange={(e) => setRates({ ...rates, vehicle_rental_percentage: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Vehicle maintenance % (of available)</Form.Label>
                    <Form.Control
                      type="number" step="0.01" min="0" max="1" value={rates.vehicle_maintenance_percentage}
                      onChange={(e) => setRates({ ...rates, vehicle_maintenance_percentage: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {effectiveFuelRate && (
                <p className="text-muted">Effective fuel-program rate: <strong>${effectiveFuelRate}/km</strong></p>
              )}
              <Button onClick={saveRates} disabled={savingRates}>
                {savingRates ? 'Saving…' : 'Save Rates'}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Per-Driver Vehicle Ownership / Management Status</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Driver ID</Form.Label>
            <Row>
              <Col md={4}>
                <Form.Control
                  value={driverId} onChange={(e) => setDriverId(e.target.value)}
                  placeholder="e.g. AAA-100106"
                />
              </Col>
              <Col md={2}>
                <Button variant="secondary" onClick={lookupDriver} disabled={loadingStatus || !driverId.trim()}>
                  {loadingStatus ? 'Loading…' : 'Look up'}
                </Button>
              </Col>
            </Row>
          </Form.Group>

          {status && (
            <>
              <p>Current status: <Badge bg="info">{TIERS.find(t => t.value === status.tier)?.label || status.tier}</Badge>
                {status.owner_id ? <span className="text-muted"> (owner id {status.owner_id})</span> : null}
              </p>

              {statusMessage && <Alert variant={statusMessage.type}>{statusMessage.text}</Alert>}

              <Form.Group className="mb-3">
                <Form.Label>Set tier to</Form.Label>
                {TIERS.map((t) => (
                  <Form.Check
                    key={t.value}
                    type="radio"
                    id={`tier-${t.value}`}
                    name="tier"
                    label={<span><strong>{t.label}</strong> — <span className="text-muted">{t.description}</span></span>}
                    checked={tierChoice === t.value}
                    onChange={() => setTierChoice(t.value)}
                  />
                ))}
              </Form.Group>

              {tierChoice === 'owner_financed_xgo_managed' && !status.owner_id && (
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Vehicle owner name</Form.Label>
                      <Form.Control value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Vehicle owner phone</Form.Label>
                      <Form.Control value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} />
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Button onClick={saveStatus} disabled={savingStatus}>
                {savingStatus ? 'Saving…' : 'Update Status'}
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default VehicleOwnershipSettings;
